'use client';

import { useEffect } from 'react';
import { getSlugForCountry } from '@/lib/i18n';
import { getLangPref, ORIGIN_KEY } from '@/lib/user-preferences';
import type { CountryCode } from '@/lib/types';

export const GEO_CACHE_KEY = 'vacant-day-geo-country';
/** Cookie name readable by middleware for server-side geo redirect */
export const GEO_COOKIE = 'geo-country';
const GEO_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 7 days
const GEO_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 days in seconds

/** Map ISO 3166-1 alpha-2 codes to our supported CountryCode values */
const GEO_TO_COUNTRY: Record<string, CountryCode> = {
  ID: 'ID', MY: 'MY', SG: 'SG', MM: 'MM',
  AF: 'AF', IN: 'IN', ET: 'ET', MN: 'MN',
  LA: 'LA', SD: 'SD', YE: 'YE', SY: 'SY', IQ: 'IQ',
  TH: 'TH', VN: 'VN', PH: 'PH', TR: 'TR',
  AE: 'AE', EG: 'EG', SA: 'SA', AU: 'AU',
  GB: 'GB', US: 'US',
};

/**
 * Detect the visitor's country via IP geolocation (non-redirecting).
 * Returns the detected CountryCode or null. Caches result in localStorage.
 */
export async function detectGeoCountry(signal?: AbortSignal): Promise<CountryCode | null> {
  // Check localStorage cache first
  const cached = localStorage.getItem(GEO_CACHE_KEY);
  if (cached) {
    try {
      const { code, ts } = JSON.parse(cached);
      if (Date.now() - ts < GEO_CACHE_TTL) {
        return GEO_TO_COUNTRY[code] ?? null;
      }
    } catch { /* invalid cache — refetch */ }
  }

  try {
    const res = await fetch('https://ipapi.co/json/', {
      signal: signal ?? AbortSignal.timeout(3000),
    });
    if (!res.ok) return null;
    const data = await res.json();
    if (!data?.country_code) return null;

    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ code: data.country_code, ts: Date.now() }));
    // Set cookie so middleware can redirect server-side on next visit
    const mapped = GEO_TO_COUNTRY[data.country_code];
    if (mapped) {
      document.cookie = `${GEO_COOKIE}=${mapped}; path=/; max-age=${GEO_COOKIE_MAX_AGE}; SameSite=Lax`;
    }
    return mapped ?? null;
  } catch {
    return null;
  }
}

/**
 * Detect the visitor's preferred language from the browser.
 * Returns a supported locale code ('en' | 'id') or the raw browser lang prefix.
 */
export function detectBrowserLang(): string {
  const override = getLangPref();
  if (override) return override;
  return navigator.language?.split('-')[0]?.toLowerCase() || 'en';
}

/**
 * On the homepage, detect the visitor's country via IP geolocation
 * and redirect to the appropriate country page.
 */
export function useAutoCountry(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    // Resolve preferred language: stored override > browser > 'en'
    const langOverride = getLangPref();
    const browserLang = langOverride || navigator.language?.split('-')[0]?.toLowerCase() || 'en';

    function redirectToCountry(countryCode: CountryCode) {
      // Persist for server-side redirect on next visit
      document.cookie = `${GEO_COOKIE}=${countryCode}; path=/; max-age=${GEO_COOKIE_MAX_AGE}; SameSite=Lax`;
      const slug = getSlugForCountry(countryCode, browserLang);
      if (slug) {
        window.location.replace('/' + slug);
      }
    }

    // 1. Stored origin country — skip IP call entirely
    const storedOrigin = localStorage.getItem(ORIGIN_KEY);
    if (storedOrigin) {
      const countryCode = GEO_TO_COUNTRY[storedOrigin];
      if (countryCode) {
        redirectToCountry(countryCode);
        return;
      }
    }

    // 2. Geo cache — avoid IP call on repeat visits
    const cached = localStorage.getItem(GEO_CACHE_KEY);
    if (cached) {
      try {
        const { code, ts } = JSON.parse(cached);
        if (Date.now() - ts < GEO_CACHE_TTL) {
          const countryCode = GEO_TO_COUNTRY[code];
          if (countryCode) {
            redirectToCountry(countryCode);
            return;
          }
        }
      } catch { /* invalid cache — refetch */ }
    }

    // 3. IP geolocation fallback
    const controller = new AbortController();

    fetch('https://ipapi.co/json/', {
      signal: AbortSignal.any([controller.signal, AbortSignal.timeout(3000)]),
    })
      .then(res => {
        if (!res.ok) return null;
        return res.json();
      })
      .then(data => {
        if (controller.signal.aborted) return;
        if (!data?.country_code) return;

        localStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ code: data.country_code, ts: Date.now() }));
        const countryCode = GEO_TO_COUNTRY[data.country_code];
        if (countryCode) {
          redirectToCountry(countryCode);
        }
      })
      .catch(() => {
        // Geolocation failed or timed out — stay on homepage
      });

    return () => controller.abort();
  }, [enabled]);
}
